import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Zap, Loader2, Building2, BellRing, CheckCircle2 } from "lucide-react";
import { matchService } from "./DayTimePicker";

const LOC_NAMES = { "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" };

const fmt12 = (t) => {
  if (!t || !t.includes(":")) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  if (Number.isNaN(hour)) return "";
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m} ${hour >= 12 ? "PM" : "AM"}`;
};

// Oct 21 2026 · Karine: "Show the soonest opening across all three clinics
// with the clinic name beside each time." Toggle lives at the top of the
// Date step. Provider-aware: Tanya/Linda flows never see Karine's openings.
const searchBody = (duration, provider, variationId) => ({
  service_duration_minutes: duration || 15,
  location_ids: ["1", "2", "3"],
  max_options: 4,
  times_per_option: 3,
  provider: provider || "karine",
  service_variation_id: variationId || null,
});

export function EarliestAnywhereToggle({ mode, onChange, isFr, clinicName, prefetch }) {
  // Warm the server-side search the moment the Date step appears so the
  // "Earliest anywhere" tap is instant (result is memoized server-side).
  useEffect(() => {
    if (!prefetch) return;
    axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/booking/fast-track-search`, searchBody(prefetch.duration, prefetch.provider, prefetch.variationId), { timeout: 45000 }).catch(() => {});
  }, [prefetch?.duration, prefetch?.provider, prefetch?.variationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const opts = [
    ["clinic", <Building2 className="w-4 h-4" key="i" />, isFr ? `Ma clinique (${clinicName})` : `My clinic (${clinicName})`],
    ["anywhere", <Zap className="w-4 h-4" key="i" />, isFr ? "Le plus tôt, toutes cliniques" : "Earliest anywhere"],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 mb-4 p-1 rounded-2xl bg-zinc-800/80 border border-zinc-700" role="tablist" data-testid="earliest-toggle">
      {opts.map(([k, icon, label]) => (
        <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => onChange(k)}
          className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-bold min-h-[48px] transition-colors ${mode === k ? "bg-amber-400 text-zinc-900" : "text-zinc-300 hover:bg-zinc-700"}`}
          data-testid={`earliest-toggle-${k}`}>
          {icon}<span className="truncate">{label}</span>
        </button>
      ))}
    </div>
  );
}

// Oct 21 2026 · Karine: "let clients sign up for alerts if sooner slots open
// up from the Earliest Anywhere list". Registers a provider-aware watcher;
// the hourly sweep texts + emails the moment something earlier opens.
function SoonerAlertCard({ cutoffDate, locationIds, duration, provider, variationId, serviceName, prefill, isFr }) {
  const [form, setForm] = useState({ name: prefill?.name || "", email: prefill?.email || "", phone: prefill?.phone || "" });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const canSave = form.email.trim().includes("@") || form.phone.replace(/\D/g, "").length >= 10;
  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/booking/fast-track-watch`, {
        client_email: form.email.trim() || `${form.phone.replace(/\D/g, "")}@sms.karinelauzon.com`,
        client_name: form.name.trim() || null,
        client_phone: form.phone.trim() || null,
        service_duration_minutes: duration || 15,
        current_earliest_date: cutoffDate,
        location_ids: locationIds,
        language: isFr ? "fr" : "en",
        provider: provider || "karine",
        service_variation_id: variationId || null,
        service_name: serviceName || null,
      }, { timeout: 15000 });
      setSaved(true);
    } catch { /* keep the form open so they can retry */ } finally { setSaving(false); }
  };
  if (saved) {
    return (
      <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center gap-3 text-emerald-100" data-testid="sooner-alert-saved">
        <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0" />
        <p className="text-sm">{isFr ? "C'est noté — nous vous préviendrons par texto et courriel dès qu'une place plus tôt s'ouvre." : "You're on the list — we'll text and email you the moment an earlier opening appears."}</p>
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-3" data-testid="sooner-alert-card">
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 text-left min-h-[44px]" data-testid="sooner-alert-toggle" aria-expanded={open}>
        <BellRing className="w-5 h-5 text-amber-300 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-white font-bold text-sm">{isFr ? "Rien d'assez tôt ? Prévenez-moi si une place s'ouvre" : "Nothing soon enough? Tell me if something sooner opens"}</p>
          <p className="text-zinc-300 text-xs">{isFr ? "Texto + courriel dès qu'une annulation libère une place plus tôt." : "Text + email the moment a cancellation frees an earlier slot."}</p>
        </div>
        <span className="text-amber-300 text-xs font-bold">{open ? (isFr ? "Fermer" : "Close") : (isFr ? "M'inscrire" : "Sign up")}</span>
      </button>
      {open && (
        <div className="mt-3 grid gap-2" data-testid="sooner-alert-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isFr ? "Prénom" : "First name"} className="rounded-xl bg-zinc-900 border border-zinc-600 px-3 py-3 text-base text-white placeholder:text-zinc-500 min-h-[48px]" data-testid="sooner-alert-name" />
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={isFr ? "Téléphone cellulaire (pour le texto)" : "Mobile number (for the text)"} className="rounded-xl bg-zinc-900 border border-zinc-600 px-3 py-3 text-base text-white placeholder:text-zinc-500 min-h-[48px]" data-testid="sooner-alert-phone" />
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={isFr ? "Courriel" : "Email"} className="rounded-xl bg-zinc-900 border border-zinc-600 px-3 py-3 text-base text-white placeholder:text-zinc-500 min-h-[48px]" data-testid="sooner-alert-email" />
          <button type="button" onClick={save} disabled={!canSave || saving} className="rounded-xl bg-amber-400 disabled:bg-zinc-700 disabled:text-zinc-400 text-zinc-900 font-bold px-4 py-3 min-h-[48px] inline-flex items-center justify-center gap-2" data-testid="sooner-alert-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
            {isFr ? "Me prévenir d'une place plus tôt" : "Alert me about sooner openings"}
          </button>
          <p className="text-[11px] text-zinc-400">{isFr ? "Une seule alerte, seulement si une place plus tôt s'ouvre. Désinscription en un clic." : "One alert, only if something sooner opens. Unsubscribe in one tap."}</p>
        </div>
      )}
    </div>
  );
}

export function EarliestAnywherePanel({ duration, provider, variationId, services = [], onPick, isFr, prefill }) {
  const [state, setState] = useState({ loading: true, options: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const sig = services.map((s) => s.variation_id).join(",");
  useEffect(() => {
    let dead = false;
    let retryTimer = null;
    setState({ loading: true, options: [], error: null });
    const API = process.env.REACT_APP_BACKEND_URL;
    // Oct 22 2026 · Karine: "Couldn't check right now — what are you joking".
    // The search and the per-clinic catalog mapping now run in PARALLEL (the
    // mapping used to gate the search, so one slow catalog call = 20 s dead
    // wait), a failed search auto-retries once, and the fallback offers a
    // Try-again instead of a dead end.
    const mappingP = Promise.all(["1", "2", "3"].map(async (id) => {
      try {
        const r = await axios.get(`${API}/api/square/catalog-services/${id}`, { timeout: 20000 });
        const list = r.data?.services || [];
        const mapped = services.map((svc) => matchService(list, svc));
        return mapped.every(Boolean) ? { id, mapped } : null;
      } catch { return null; }
    })).then((rows) => Object.fromEntries(rows.filter(Boolean).map((e) => [e.id, e.mapped]))).catch(() => ({}));
    const search = () => axios.post(`${API}/api/booking/fast-track-search`, searchBody(duration, provider, variationId), { timeout: 45000 });
    const run = (isRetry) => search().then(async (r) => {
      const mappedBy = await mappingP;
      if (dead) return;
      const known = Object.keys(mappedBy).length > 0;
      const options = (r.data?.options || [])
        .filter((o) => !known || mappedBy[String(o.location_id)])
        .map((o) => ({ ...o, mapped: mappedBy[String(o.location_id)] || null }));
      setState({ loading: false, options, error: null });
    }).catch(() => {
      if (dead) return;
      if (!isRetry) { retryTimer = setTimeout(() => run(true), 2500); return; }
      setState({ loading: false, options: [], error: isFr ? "La vérification a pris trop de temps." : "That took too long to check." });
    });
    run(false);
    return () => { dead = true; if (retryTimer) clearTimeout(retryTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, provider, variationId, sig, isFr, attempt]);

  if (state.loading) {
    return (
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 px-4 py-5 flex items-center gap-3 text-amber-100" data-testid="earliest-loading">
        <Loader2 className="w-5 h-5 animate-spin text-amber-300" />
        <span className="text-sm">{isFr ? "Recherche des premières places dans les 3 cliniques… (jusqu'à 30 s)" : "Checking the soonest openings at all 3 clinics… this can take up to 30 seconds"}</span>
      </div>
    );
  }
  if (state.error || state.options.length === 0) {
    return (
      <div className="rounded-2xl border border-zinc-700 bg-zinc-800/60 px-4 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="earliest-empty">
        <p className="text-sm text-zinc-300">{state.error || (isFr ? "Aucune ouverture trouvée pour l'instant." : "No openings found right now.")} {isFr ? "Vous pouvez aussi choisir un jour ci-dessous." : "You can also pick a day below."}</p>
        <button type="button" onClick={() => setAttempt((n) => n + 1)} className="rounded-xl bg-amber-400 hover:bg-amber-300 text-zinc-900 font-bold px-4 py-2.5 text-sm min-h-[44px]" data-testid="earliest-retry">
          {isFr ? "Réessayer" : "Try again"}
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-2 mb-2" data-testid="earliest-results">
      <p className="text-xs uppercase tracking-widest text-amber-300 font-bold">{isFr ? "Les premières places · toutes cliniques" : "Soonest openings · all clinics"}</p>
      {state.options.map((o, i) => {
        const locName = o.location_name || LOC_NAMES[o.location_id] || o.location_id;
        const times = (o.times && o.times.length ? o.times : [o.start_time]).filter(Boolean);
        return (
          <div key={`${o.location_id}-${o.date}`} className={`rounded-2xl border px-4 py-3 ${i === 0 ? "border-amber-400 bg-amber-400/10" : "border-zinc-700 bg-zinc-800/60"}`} data-testid={`earliest-option-${i}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <p className="text-white font-bold text-base">{o.formatted || o.date}</p>
              <span className="inline-flex items-center gap-1 rounded-full bg-zinc-900/70 border border-zinc-600 px-2.5 py-1 text-xs font-bold text-amber-200" data-testid={`earliest-option-${i}-clinic`}>
                <MapPin className="w-3.5 h-3.5" />{locName}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {times.map((t) => (
                <button key={t} type="button" onClick={() => onPick(o, t)}
                  className="rounded-xl bg-white text-zinc-900 hover:bg-amber-300 font-bold px-4 py-3 text-base min-h-[48px] touch-manipulation"
                  data-testid={`earliest-time-${o.location_id}-${o.date}-${t}`}>
                  {fmt12(t)} <span className="text-xs font-semibold text-zinc-600">· {locName}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <SoonerAlertCard
        cutoffDate={state.options[0]?.date}
        locationIds={[...new Set(state.options.map((o) => String(o.location_id)))]}
        duration={duration}
        provider={provider}
        variationId={variationId}
        serviceName={services[0]?.name || null}
        prefill={prefill}
        isFr={isFr}
      />
    </div>
  );
}
